import classNames from 'classnames';
import toast from 'react-hot-toast';
import { ColorId } from 'referral-types';
import { text } from 'stream/consumers';

export const ScrollableAnchorId = 'scroll-top_anchor';

export const LOCAL_STORAGE_USER_KEY = 'referral_user';

export const IS_DARK_MODE_LOCAL_STOREAGE_OVERRIDE_KEY = 'IS_DARK_MODE_OVERRIDE';
export const IS_DARK_MODE_LOCAL_STOREAGE_KEY = 'IS_DARK_MODE';

export const DEFAULT_PRIMARY_COLOR = '#2b7fff';
export const DEFAULT_SECONDARY_COLOR = '#d97706';
export const DEFAULT_TERTIARY_COLOR = '#fb2c36';

export function getReferralRowsClassnames(layout?: Layout) {
  const forceCompactMode = layout === Layout.compact;
  const responsiveClasses = '@xl:grid-cols-2 @5xl:grid-cols-3 gap-x-6 @5xl:gap-x-8';

  return classNames('w-full grid grid-cols-1', forceCompactMode && responsiveClasses);
}

export function getReferralColsClassnames(size?: Size.sm | Size.lg): string {
  return classNames(
    'w-full flex flex-col justify-center items-start',
    size === Size.sm && '@sm:flex-row @sm:justify-stretch',
    size === Size.lg && '@xl:flex-row @xl:justify-stretch',
    !size && '@md:flex-row @md:justify-stretch'
  );
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export function getMediaSize(size: Size, ratio: Ratio): string {
  const isSquare = ratio === Ratio.square;
  // console.log(`${size} ${ratio}`);
  // if (ratio === Ratio.rectangle) {
  //     switch (size) {

  //         default:
  //             return 'w-[300px] h-[188px] md:w-[200px] md:h-[125px] lg:w-[248px] lg:h-[155px] xl:w-[300px] xl:h-[188px]'
  //     }
  // }
  // else {
  //     return '';
  // }

  return classNames(
    'max-w-full',
    isSquare ? 'aspect-square' : 'aspect-[75/47]',
    size === Size.xl ? 'w-full' : 'w-[469px] @md:w-[100px] @xl:w-[200px] @3xl:w-[248px] @5xl:w-[300px]'
  );
}

export const BROADCAST_USER_CHANNEL = 'referral_user_channel';
export const BroadcastChannelActions = {
  UPDATE_USER: 'UPDATE_USER',
};

// todo put in referral-types

export type IconType = React.ForwardRefExoticComponent<
  Omit<React.SVGProps<SVGSVGElement>, 'ref'> & {
    title?: string | undefined;
    titleId?: string | undefined;
  } & React.RefAttributes<SVGSVGElement>
>;

export type AnimationProps = {
  enter?: string;
  enterFrom?: string;
  enterTo?: string;
  leave?: string;
  leaveFrom?: string;
  leaveTo?: string;
  whileHover?: { scale: number };
  whileTap?: { scale: number };
};

export const ACCORDION_TRANSITION = {
  initial: { height: 0, opacity: 0 },
  animate: { height: 'auto', opacity: 1 },
  exit: { height: 0, opacity: 0 },
  style: { overflow: 'hidden' },
};

export const ACCORDION_TRANSITION_ANTICIPATE = {
  ...ACCORDION_TRANSITION,
  transition: { duration: 0.5, ease: 'anticipate' },
};

export const ACCORDION_TRANSITION_X = {
  initial: { width: 0, opacity: 0 },
  animate: { width: 'fit-content', opacity: 1 },
  exit: { width: 0, opacity: 0 },
};
export const ACCORDION_TRANSITION_X_ANTICIPATE = {
  ...ACCORDION_TRANSITION_X,
  transition: { duration: 0.5, ease: 'anticipate' },
};

export enum Ratio {
  square = 'square',
  rectangle = 'rectangle',
}

export const RatioOptions = [
  { value: Ratio.rectangle, label: 'Rectangle' },
  { value: Ratio.square, label: 'Square' },
];

export enum LogoTheme {
  primary = 'primary',
  secondary = 'secondary',
  primaryLight = 'primary-light',
  intense = 'intense',
  muted = 'muted',
  light = 'light',
  lightColorless = 'lightColorless',
  dark = 'dark',
  darkColorless = 'darkColorless',
  inverted = 'inverted',
}

export const LogoThemeOptions = [
  { value: LogoTheme.primary, label: 'Primary' },
  { value: LogoTheme.primaryLight, label: 'Primary Light' },
  { value: LogoTheme.muted, label: 'Muted' },
  { value: LogoTheme.intense, label: 'Intense' },
  { value: LogoTheme.light, label: 'Light' },
  { value: LogoTheme.dark, label: 'Dark' },
  { value: LogoTheme.inverted, label: 'Inverted' },
];

export enum LogoStyle {
  default = 'default',
  custom = 'custom',
}

export enum Size {
  none = 'none',
  xs = 'xs',
  sm = 'sm',
  md = 'md',
  lg = 'lg',
  xl = 'xl',
}

export const ImageSizeOptions = [
  { value: Size.none, label: 'None' },
  { value: Size.xs, label: 'Extra Small' },
  { value: Size.sm, label: 'Small' },
  { value: Size.md, label: 'Medium' },
  { value: Size.lg, label: 'Large' },
  { value: Size.xl, label: 'Extra Large' },
];

export enum Layout {
  expanded = 'expanded',
  compact = 'compact',
}

export const LayoutOptions = [
  { value: Layout.expanded, label: 'Rows' },
  { value: Layout.compact, label: 'Columns' },
];

export enum PageLayout {
  standard = 'standard',
  sidebar = 'sidebar',
}

export const PageLayoutOptions = [
  { value: PageLayout.standard, label: 'Classic' },
  { value: PageLayout.sidebar, label: 'Sidebar' },
];

export enum Enabled {
  enabled = 'enabled',
  disabled = 'disabled',
}

export const EnabledOptions = [
  { value: Enabled.enabled, label: 'Enabled' },
  { value: Enabled.disabled, label: 'Disabled' },
];

export enum Weight {
  bold = 'bold',
  semibold = 'semibold',
  regular = 'regular',
}

export const WeightOptions = [
  { value: Weight.regular, label: 'Regular' },
  { value: Weight.semibold, label: 'Semibold' },
  { value: Weight.bold, label: 'Bold' },
];

export enum Font {
  inter = 'Inter',
  merriweather = 'Merriweather',
  montserrat = 'Montserrat',
  poppins = 'Poppins',
  nunito = 'Nunito',
  playfairDisplay = 'Playfair+Display',
  lora = 'Lora',
  bitter = 'Bitter',
  barlow = 'Barlow',
  ibmPlexSans = 'IBM+Plex+Sans',
  patrickHand = 'Patrick+Hand',
  sora = 'Sora',
}

export const FontOptions = [
  { value: Font.inter, label: 'Inter' },
  { value: Font.merriweather, label: 'Merriweather' },
  { value: Font.montserrat, label: 'Montserrat' },
  { value: Font.poppins, label: 'Poppins' },
  { value: Font.nunito, label: 'Nunito' },
  { value: Font.playfairDisplay, label: 'Playfair Display' },
  { value: Font.lora, label: 'Lora' },
  { value: Font.bitter, label: 'Bitter' },
  { value: Font.barlow, label: 'Barlow' },
  { value: Font.ibmPlexSans, label: 'IBM Plex Sans' },
  { value: Font.patrickHand, label: 'Patrick Hand' },
  { value: Font.sora, label: 'Sora' },
];

export const PaddingOptions = [
  { value: Enabled.disabled, label: 'None' },
  { value: Enabled.enabled, label: 'Padded' },
];

export const SocialPaddingOptions = [
  { value: Size.none, label: 'None' },
  { value: Size.sm, label: 'Less' },
  { value: Size.md, label: 'Some' },
  { value: Size.lg, label: 'More' },
  { value: Size.xl, label: 'Extra' },
];

export enum InitialDisplay {
  expanded = 'expanded',
  collapsed = 'collapsed',
}

export const InitialDisplayOptions = [
  { value: InitialDisplay.expanded, label: 'Expanded' },
  { value: InitialDisplay.collapsed, label: 'Collapsed' },
];

export const CardCornerSizeOptions = [
  { value: Size.none, label: 'None' },
  { value: Size.xs, label: 'Less' },
  { value: Size.sm, label: 'Less' },
  { value: Size.md, label: 'Rounded' },
  { value: Size.lg, label: 'More' },
  { value: Size.xl, label: 'Circular' },
];

export const TitleSizeOptions = [
  { value: Size.sm, label: 'Small' },
  { value: Size.md, label: 'Medium' },
  { value: Size.lg, label: 'Large' },
];

export const ClampSizeOptions = [
  { value: Size.none, label: 'None' },
  { value: Size.xs, label: '1 Line' },
  { value: Size.sm, label: '2 Lines' },
  { value: Size.md, label: '3 Lines' },
  { value: Size.lg, label: '5 Lines' },
  { value: Size.xl, label: '8 Lines' },
];

export const BorderSizeOptions = [
  { value: Size.none, label: 'None' },
  { value: Size.sm, label: 'Small' },
  { value: Size.md, label: 'Medium' },
  { value: Size.lg, label: 'Large' },
];

export const BlurSizeOptions = [
  { value: Size.none, label: 'None' },
  { value: Size.sm, label: 'Less' },
  { value: Size.md, label: 'Some' },
  { value: Size.lg, label: 'More' },
  { value: Size.xl, label: 'Extra' },
];

export function getCornerStyleClassname(cornerStyle?: Size, mobileCorners?: boolean): string {
  switch (cornerStyle) {
    case Size.xs:
      return mobileCorners ? 'sm:rounded-sm' : 'rounded-sm';
    case Size.sm:
      return mobileCorners ? 'sm:rounded-md' : 'rounded-md';
    case Size.lg:
      return mobileCorners ? 'sm:rounded-3xl' : 'rounded-3xl';
    case Size.none:
      return 'rounded-none';
    case Size.xl:
      return mobileCorners ? 'sm:rounded-full' : 'rounded-full';
    case Size.md:
    default:
      return mobileCorners ? 'sm:rounded-xl' : 'rounded-xl';
  }
}

export function getCornerStyleTopClassname(cornerStyle?: Size, mobileCorners?: boolean): string {
  switch (cornerStyle) {
    case Size.xs:
      return mobileCorners ? 'sm:rounded-t-sm' : 'rounded-t-sm';
    case Size.sm:
      return mobileCorners ? 'sm:rounded-t-md' : 'rounded-t-md';
    case Size.lg:
      return mobileCorners ? 'sm:rounded-t-3xl' : 'rounded-t-3xl';
    case Size.none:
      return 'rounded-none';
    case Size.xl:
      return mobileCorners ? 'sm:rounded-t-full' : 'rounded-t-full';
    case Size.md:
    default:
      return mobileCorners ? 'sm:rounded-t-xl' : 'rounded-t-xl';
  }
}

export function getOpacityBlurClassName(opacityBlur?: Size): string {
  switch (opacityBlur) {
    case Size.sm:
      return 'backdrop-blur-sm';
    case Size.md:
      return 'backdrop-blur';
    case Size.lg:
      return 'backdrop-blur-lg';
    case Size.xl:
      return 'backdrop-blur-xl';
    case Size.none:
    default:
      return 'backdrop-blur-none';
  }
}

export function getBorderSizeClass(size?: Size): string {
  switch (size) {
    case Size.sm:
      return 'border';
    case Size.md:
      return 'border-2';
    case Size.lg:
      return 'border-4';
    case Size.none:
    default:
      return 'border-0';
  }
}

export enum MediaOption {
  none = 'none',
  custom = 'custom',
  presetSvg = 'preset-svg',
  presetImage = 'preset-image',
  presetVideo = 'preset-video',
}

export enum MediaRepeat {
  none = 'none',
  repeat = 'repeat',
  repeatX = 'repeatX',
  repeatY = 'repeatY',
}

export const MediaRepeatOptions = [
  { value: MediaRepeat.none, label: 'None' },
  { value: MediaRepeat.repeat, label: 'Repeat' },
  { value: MediaRepeat.repeatX, label: 'Repeat X' },
  { value: MediaRepeat.repeatY, label: 'Repeat Y' },
];

export enum MediaSize {
  cover = 'cover',
  contain = 'contain',
  auto = 'auto',
}

export const MediaSizeOptions = [
  { value: MediaSize.cover, label: 'Cover' },
  { value: MediaSize.contain, label: 'Contain' },
  { value: MediaSize.auto, label: 'Auto' },
];

export enum MediaPosition {
  center = 'center',
  left = 'left',
  right = 'right',
  top = 'top',
  bottom = 'bottom',
}

export const MediaPositionOptions = [
  { value: MediaPosition.center, label: 'Center' },
  { value: MediaPosition.left, label: 'Left' },
  { value: MediaPosition.right, label: 'Right' },
];

export enum MediaAttachment {
  scroll = 'scroll',
  fixed = 'fixed',
}

export const MediaAttachmentOptions = [
  { value: MediaAttachment.scroll, label: 'Scroll' },
  { value: MediaAttachment.fixed, label: 'Fixed' },
];

export enum CollectionTier {
  lite,
  pro,
  ultra,
}

export const CollectionTierOptions = [
  { value: CollectionTier.lite, label: 'Lite' },
  { value: CollectionTier.pro, label: 'Pro' },
  { value: CollectionTier.ultra, label: 'Ultra' },
];

export enum LightMode {
  light = 'light',
  dark = 'dark',
  any = 'any',
}

export const LightModeOptions = [
  { value: LightMode.light, label: 'Forced Light Mode' },
  { value: LightMode.dark, label: 'Forced Dark Mode' },
  { value: LightMode.any, label: 'Visitor Preferrence' },
];

export enum NavbarStyle {
  default = 'default',
  minimalist = 'minimalist',
}

export const NavbarStyleOptions = [
  { value: NavbarStyle.default, label: 'Default' },
  { value: NavbarStyle.minimalist, label: 'Minimalist' },
];

export enum OpacityLevel {
  opacity0 = '0%',
  opacity10 = '10%',
  opacity20 = '20%',
  opacity30 = '30%',
  opacity40 = '40%',
  opacity50 = '50%',
  opacity60 = '60%',
  opacity70 = '70%',
  opacity80 = '80%',
  opacity90 = '90%',
  opacity100 = '100%',
}

export const OpacityOptions = [
  { value: OpacityLevel.opacity0, label: '0%' },
  { value: OpacityLevel.opacity10, label: '10%' },
  { value: OpacityLevel.opacity20, label: '20%' },
  { value: OpacityLevel.opacity30, label: '30%' },
  { value: OpacityLevel.opacity40, label: '40%' },
  { value: OpacityLevel.opacity50, label: '50%' },
  { value: OpacityLevel.opacity60, label: '60%' },
  { value: OpacityLevel.opacity70, label: '70%' },
  { value: OpacityLevel.opacity80, label: '80%' },
  { value: OpacityLevel.opacity90, label: '90%' },
  { value: OpacityLevel.opacity100, label: '100%' },
];

export enum IntensityLevel {
  intensityMinus5 = '-5',
  intensityMinus4 = '-4',
  intensityMinus3 = '-3',
  intensityMinus2 = '-2',
  intensityMinus1 = '-1',
  intensity0 = '0',
  intensityPlus1 = '+1',
  intensityPlus2 = '+2',
  intensityPlus3 = '+3',
  intensityPlus4 = '+4',
  intensityPlus5 = '+5',
}

export function getIntensityClassName(options: { intensity?: IntensityLevel; isDarkMode: boolean }): string | null {
  const { intensity, isDarkMode } = options;
  const minusClasses = isDarkMode ? 'bg-white' : 'bg-black';
  const plusClasses = isDarkMode ? 'bg-black' : 'bg-white';

  if (intensity == null) {
    return null;
  }

  switch (intensity) {
    case IntensityLevel.intensityMinus5:
      return classNames(plusClasses, 'bg-opacity-80');
    case IntensityLevel.intensityMinus4:
      return classNames(plusClasses, 'bg-opacity-60');
    case IntensityLevel.intensityMinus3:
      return classNames(plusClasses, 'bg-opacity-40');
    case IntensityLevel.intensityMinus2:
      return classNames(plusClasses, 'bg-opacity-20');
    case IntensityLevel.intensityMinus1:
      return classNames(plusClasses, 'bg-opacity-10');
    case IntensityLevel.intensityPlus1:
      return classNames(minusClasses, 'bg-opacity-10');
    case IntensityLevel.intensityPlus2:
      return classNames(minusClasses, 'bg-opacity-20');
    case IntensityLevel.intensityPlus3:
      return classNames(minusClasses, 'bg-opacity-40');
    case IntensityLevel.intensityPlus4:
      return classNames(minusClasses, 'bg-opacity-60');
    case IntensityLevel.intensityPlus5:
      return classNames(minusClasses, 'bg-opacity-80');
    default:
    case IntensityLevel.intensity0:
      return classNames(plusClasses, 'bg-opacity-0');
  }
}

export const IntensityOptions = [
  { value: IntensityLevel.intensityMinus3, label: '-3' },
  { value: IntensityLevel.intensityMinus2, label: '-2' },
  { value: IntensityLevel.intensityMinus1, label: '-1' },
  { value: IntensityLevel.intensity0, label: '0' },
  { value: IntensityLevel.intensityPlus1, label: '+1' },
  { value: IntensityLevel.intensityPlus2, label: '+2' },
  { value: IntensityLevel.intensityPlus3, label: '+3' },
];

// 1. Define the static map of all possible combinations
// This allows Tailwind to "see" the full class strings during build time.
const COLOR_STYLE_MAP: Record<string, Record<string, string>> = {
  primary1: {
    background: 'bg-user-primary-1',
    textBackground: 'bg-user-primary-1',
    textHover: 'hover:text-user-primary-2',
    backgroundHover: 'hover:bg-user-primary-2',
    border: 'border-user-primary-1',
    borderBackground: 'border-user-primary-1',
    divider: 'divide-user-primary-1',
    text: 'text-user-primary-1',
  },
  primary2: {
    background: 'bg-user-primary-2',
    textBackground: 'bg-user-primary-2',
    textHover: 'hover:text-user-primary-3',
    backgroundHover: 'hover:bg-user-primary-3',
    border: 'border-user-primary-2',
    borderBackground: 'border-user-primary-2',
    divider: 'divide-user-primary-2',
    text: 'text-user-primary-2',
  },
  primary3: {
    background: 'bg-user-primary-3',
    textBackground: 'bg-user-primary-3',
    textHover: 'hover:text-user-primary-4',
    backgroundHover: 'hover:bg-user-primary-4',
    border: 'border-user-primary-3',
    borderBackground: 'border-user-primary-3',
    divider: 'divide-user-primary-3',
    text: 'text-user-primary-3',
  },
  primary4: {
    background: 'bg-user-primary-4',
    textBackground: 'bg-user-primary-4',
    textHover: 'hover:text-user-primary-5',
    backgroundHover: 'hover:bg-user-primary-5',
    border: 'border-user-primary-4',
    borderBackground: 'border-user-primary-4',
    divider: 'divide-user-primary-4',
    text: 'text-user-primary-4',
  },
  primary5: {
    background: 'bg-user-primary-5',
    textBackground: 'bg-user-primary-5',
    textHover: 'hover:text-user-primary-4',
    backgroundHover: 'hover:bg-user-primary-6',
    border: 'border-user-primary-5',
    borderBackground: 'border-user-primary-5',
    divider: 'divide-user-primary-5',
    text: 'text-user-primary-5',
  },
  primary6: {
    background: 'bg-user-primary-6',
    textBackground: 'bg-user-primary-6',
    textHover: 'hover:text-user-primary-5',
    backgroundHover: 'hover:bg-user-primary-7',
    border: 'border-user-primary-6',
    borderBackground: 'border-user-primary-6',
    divider: 'divide-user-primary-6',
    text: 'text-user-primary-6',
  },
  primary7: {
    background: 'bg-user-primary-7',
    textBackground: 'bg-user-primary-7',
    textHover: 'hover:text-user-primary-6',
    backgroundHover: 'hover:bg-user-primary-6',
    border: 'border-user-primary-7',
    borderBackground: 'border-user-primary-7',
    divider: 'divide-user-primary-7',
    text: 'text-user-primary-7',
  },
  secondary1: {
    background: 'bg-user-secondary-1',
    textBackground: 'bg-user-secondary-1',
    textHover: 'hover:text-user-secondary-2',
    backgroundHover: 'hover:bg-user-secondary-2',
    border: 'border-user-secondary-1',
    borderBackground: 'border-user-secondary-1',
    divider: 'divide-user-secondary-1',
    text: 'text-user-secondary-1',
  },
  secondary2: {
    background: 'bg-user-secondary-2',
    textBackground: 'bg-user-secondary-2',
    textHover: 'hover:text-user-secondary-3',
    backgroundHover: 'hover:bg-user-secondary-3',
    border: 'border-user-secondary-2',
    borderBackground: 'border-user-secondary-2',
    divider: 'divide-user-secondary-2',
    text: 'text-user-secondary-2',
  },
  secondary3: {
    background: 'bg-user-secondary-3',
    textBackground: 'bg-user-secondary-3',
    textHover: 'hover:text-user-secondary-4',
    backgroundHover: 'hover:bg-user-secondary-4',
    border: 'border-user-secondary-3',
    borderBackground: 'border-user-secondary-3',
    divider: 'divide-user-secondary-3',
    text: 'text-user-secondary-3',
  },
  secondary4: {
    background: 'bg-user-secondary-4',
    textBackground: 'bg-user-secondary-4',
    textHover: 'hover:text-user-secondary-5',
    backgroundHover: 'hover:bg-user-secondary-5',
    border: 'border-user-secondary-4',
    borderBackground: 'border-user-secondary-4',
    divider: 'divide-user-secondary-4',
    text: 'text-user-secondary-4',
  },
  secondary5: {
    background: 'bg-user-secondary-5',
    textBackground: 'bg-user-secondary-5',
    textHover: 'hover:text-user-secondary-4',
    backgroundHover: 'hover:bg-user-secondary-6',
    border: 'border-user-secondary-5',
    borderBackground: 'border-user-secondary-5',
    divider: 'divide-user-secondary-5',
    text: 'text-user-secondary-5',
  },
  secondary6: {
    background: 'bg-user-secondary-6',
    textBackground: 'bg-user-secondary-6',
    textHover: 'hover:text-user-secondary-5',
    backgroundHover: 'hover:bg-user-secondary-7',
    border: 'border-user-secondary-6',
    borderBackground: 'border-user-secondary-6',
    divider: 'divide-user-secondary-6',
    text: 'text-user-secondary-6',
  },
  secondary7: {
    background: 'bg-user-secondary-7',
    textBackground: 'bg-user-secondary-7',
    textHover: 'hover:text-user-secondary-6',
    backgroundHover: 'hover:bg-user-secondary-6',
    border: 'border-user-secondary-7',
    borderBackground: 'border-user-secondary-7',
    divider: 'divide-user-secondary-7',
    text: 'text-user-secondary-7',
  },
  tertiary1: {
    background: 'bg-user-tertiary-1',
    textBackground: 'bg-user-tertiary-1',
    textHover: 'hover:text-user-tertiary-2',
    backgroundHover: 'hover:bg-user-tertiary-2',
    border: 'border-user-tertiary-1',
    borderBackground: 'border-user-tertiary-1',
    divider: 'divide-user-tertiary-1',
    text: 'text-user-tertiary-1',
  },
  tertiary2: {
    background: 'bg-user-tertiary-2',
    textBackground: 'bg-user-tertiary-2',
    textHover: 'hover:text-user-tertiary-3',
    backgroundHover: 'hover:bg-user-tertiary-3',
    border: 'border-user-tertiary-2',
    borderBackground: 'border-user-tertiary-2',
    divider: 'divide-user-tertiary-2',
    text: 'text-user-tertiary-2',
  },
  tertiary3: {
    background: 'bg-user-tertiary-3',
    textBackground: 'bg-user-tertiary-3',
    textHover: 'hover:text-user-tertiary-4',
    backgroundHover: 'hover:bg-user-tertiary-4',
    border: 'border-user-tertiary-3',
    borderBackground: 'border-user-tertiary-3',
    divider: 'divide-user-tertiary-3',
    text: 'text-user-tertiary-3',
  },
  tertiary4: {
    background: 'bg-user-tertiary-4',
    textBackground: 'bg-user-tertiary-4',
    textHover: 'hover:text-user-tertiary-5',
    backgroundHover: 'hover:bg-user-tertiary-5',
    border: 'border-user-tertiary-4',
    borderBackground: 'border-user-tertiary-4',
    divider: 'divide-user-tertiary-4',
    text: 'text-user-tertiary-4',
  },
  tertiary5: {
    background: 'bg-user-tertiary-5',
    textBackground: 'bg-user-tertiary-5',
    textHover: 'hover:text-user-tertiary-4',
    backgroundHover: 'hover:bg-user-tertiary-6',
    border: 'border-user-tertiary-5',
    borderBackground: 'border-user-tertiary-5',
    divider: 'divide-user-tertiary-5',
    text: 'text-user-tertiary-5',
  },
  tertiary6: {
    background: 'bg-user-tertiary-6',
    textBackground: 'bg-user-tertiary-6',
    textHover: 'hover:text-user-tertiary-5',
    backgroundHover: 'hover:bg-user-tertiary-7',
    border: 'border-user-tertiary-6',
    borderBackground: 'border-user-tertiary-6',
    divider: 'divide-user-tertiary-6',
    text: 'text-user-tertiary-6',
  },
  tertiary7: {
    background: 'bg-user-tertiary-7',
    textBackground: 'bg-user-tertiary-7',
    textHover: 'hover:text-user-tertiary-6',
    backgroundHover: 'hover:bg-user-tertiary-6',
    border: 'border-user-tertiary-7',
    borderBackground: 'border-user-tertiary-7',
    divider: 'divide-user-tertiary-7',
    text: 'text-user-tertiary-7',
  },
  gray1: {
    background: 'bg-user-gray-1',
    textBackground: 'bg-user-gray-1',
    textHover: 'hover:text-user-gray-2',
    backgroundHover: 'hover:bg-user-gray-2',
    border: 'border-user-gray-1',
    borderBackground: 'border-user-gray-1',
    divider: 'divide-user-gray-1',
    text: 'text-user-gray-1',
  },
  gray2: {
    background: 'bg-user-gray-2',
    textBackground: 'bg-user-gray-2',
    textHover: 'hover:text-user-gray-3',
    backgroundHover: 'hover:bg-user-gray-3',
    border: 'border-user-gray-2',
    borderBackground: 'border-user-gray-2',
    divider: 'divide-user-gray-2',
    text: 'text-user-gray-2',
  },
  gray3: {
    background: 'bg-user-gray-3',
    textBackground: 'bg-user-gray-3',
    textHover: 'hover:text-user-gray-4',
    backgroundHover: 'hover:bg-user-gray-4',
    border: 'border-user-gray-3',
    borderBackground: 'border-user-gray-3',
    divider: 'divide-user-gray-3',
    text: 'text-user-gray-3',
  },
  gray4: {
    background: 'bg-user-gray-4',
    textBackground: 'bg-user-gray-4',
    textHover: 'hover:text-user-gray-5',
    backgroundHover: 'hover:bg-user-gray-5',
    border: 'border-user-gray-4',
    borderBackground: 'border-user-gray-4',
    divider: 'divide-user-gray-4',
    text: 'text-user-gray-4',
  },
  gray5: {
    background: 'bg-user-gray-5',
    textBackground: 'bg-user-gray-5',
    textHover: 'hover:text-user-gray-4',
    backgroundHover: 'hover:bg-user-gray-6',
    border: 'border-user-gray-5',
    borderBackground: 'border-user-gray-5',
    divider: 'divide-user-gray-5',
    text: 'text-user-gray-5',
  },
  gray6: {
    background: 'bg-user-gray-6',
    textBackground: 'bg-user-gray-6',
    textHover: 'hover:text-user-gray-5',
    backgroundHover: 'hover:bg-user-gray-7',
    border: 'border-user-gray-6',
    borderBackground: 'border-user-gray-6',
    divider: 'divide-user-gray-6',
    text: 'text-user-gray-6',
  },
  gray7: {
    background: 'bg-user-gray-7',
    textBackground: 'bg-user-gray-7',
    textHover: 'hover:text-user-gray-6',
    backgroundHover: 'hover:bg-user-gray-6',
    border: 'border-user-gray-7',
    borderBackground: 'border-user-gray-7',
    divider: 'divide-user-gray-7',
    text: 'text-user-gray-7',
  },
};

export function getColorStyleClassName(options: {
  colorId?: ColorId;
  type:
    | 'border'
    | 'text'
    | 'textHover'
    | 'divider'
    | 'background'
    | 'backgroundHover'
    | 'borderBackground'
    | 'textBackground';
}): string | undefined {
  const { colorId = ColorId.Gray1, type } = options;

  // Use the static lookup map.
  // Since colorId is likely a string enum like 'primary4', we can use it directly as the key.
  const stylesForColor = COLOR_STYLE_MAP[colorId];

  if (!stylesForColor) {
    // Fallback or error handling if an unknown colorId is passed
    return '';
  }

  return stylesForColor[type] || undefined;
}

/**
 * Maps the 1-7 Step to the actual raw shade variable for style props or custom CSS
 */
export function getCssVariableForColorId(colorId: ColorId, isDarkMode: boolean): string {
  const family = colorId.replace(/[0-9]/g, '');
  const step = parseInt(colorId.replace(/\D/g, ''), 10);
  const shades = [50, 200, 400, 600, 800, 900, 950];
  if (isDarkMode) {
    shades.reverse();
  }
  const targetShade = shades[step - 1] || 600; // reverse if darkmode

  // Matches your specific naming convention
  return family === 'gray' ? `--gray-${targetShade}` : `--${family}-${targetShade}`;
}

export function getOpacityClassName(opacityLevel?: OpacityLevel, type?: 'background' | 'after'): string | null {
  if (opacityLevel == null) {
    return null;
  }

  switch (opacityLevel) {
    case OpacityLevel.opacity10:
      switch (type) {
        case 'after':
          return 'after:opacity-10';
        case 'background':
          return 'bg-opacity-10';
        default:
          return 'opacity-10';
      }
    case OpacityLevel.opacity20:
      switch (type) {
        case 'after':
          return 'after:opacity-20';
        case 'background':
          return 'bg-opacity-20';
        default:
          return 'opacity-20';
      }
    case OpacityLevel.opacity30:
      switch (type) {
        case 'after':
          return 'after:opacity-30';
        case 'background':
          return 'bg-opacity-30';
        default:
          return 'opacity-30';
      }
    case OpacityLevel.opacity40:
      switch (type) {
        case 'after':
          return 'after:opacity-40';
        case 'background':
          return 'bg-opacity-40';
        default:
          return 'opacity-40';
      }
    case OpacityLevel.opacity50:
      switch (type) {
        case 'after':
          return 'after:opacity-50';
        case 'background':
          return 'bg-opacity-50';
        default:
          return 'opacity-50';
      }
    case OpacityLevel.opacity60:
      switch (type) {
        case 'after':
          return 'after:opacity-60';
        case 'background':
          return 'bg-opacity-60';
        default:
          return 'opacity-60';
      }
    case OpacityLevel.opacity70:
      switch (type) {
        case 'after':
          return 'after:opacity-70';
        case 'background':
          return 'bg-opacity-70';
        default:
          return 'opacity-70';
      }
    case OpacityLevel.opacity80:
      switch (type) {
        case 'after':
          return 'after:opacity-80';
        case 'background':
          return 'bg-opacity-80';
        default:
          return 'opacity-80';
      }
    case OpacityLevel.opacity90:
      switch (type) {
        case 'after':
          return 'after:opacity-90';
        case 'background':
          return 'bg-opacity-90';
        default:
          return 'opacity-90';
      }
    case OpacityLevel.opacity100:
      switch (type) {
        case 'after':
          return 'after:opacity-100';
        case 'background':
          return 'bg-opacity-100';
        default:
          return 'opacity-100';
      }
    case OpacityLevel.opacity0:
    default:
      switch (type) {
        case 'after':
          return 'after:opacity-0';
        case 'background':
          return 'bg-opacity-0';
        default:
          return 'opacity-0';
      }
  }
}
export function getOpacityStyle(opacityLevel?: OpacityLevel): number {
  if (opacityLevel == null) {
    return 1.0;
  }

  switch (opacityLevel) {
    case OpacityLevel.opacity10:
      return 0.1;
    case OpacityLevel.opacity20:
      return 0.2;
    case OpacityLevel.opacity30:
      return 0.3;
    case OpacityLevel.opacity40:
      return 0.4;
    case OpacityLevel.opacity50:
      return 0.5;
    case OpacityLevel.opacity60:
      return 0.6;
    case OpacityLevel.opacity70:
      return 0.7;
    case OpacityLevel.opacity80:
      return 0.8;
    case OpacityLevel.opacity90:
      return 0.9;
    case OpacityLevel.opacity100:
      return 1.0;
    case OpacityLevel.opacity0:
    default:
      return 0.0;
  }
}

export function getOpacityLevelFromSlider(sliderInput: number): OpacityLevel {
  switch (sliderInput) {
    case 10:
      return OpacityLevel.opacity10;
    case 20:
      return OpacityLevel.opacity20;
    case 30:
      return OpacityLevel.opacity30;
    case 40:
      return OpacityLevel.opacity40;
    case 50:
      return OpacityLevel.opacity50;
    case 60:
      return OpacityLevel.opacity60;
    case 70:
      return OpacityLevel.opacity70;
    case 80:
      return OpacityLevel.opacity80;
    case 90:
      return OpacityLevel.opacity90;
    case 100:
      return OpacityLevel.opacity100;
    case 0:
    default:
      return OpacityLevel.opacity0; // Default to fully transparent if no match
  }
}

// Title size scales. Group titles render one step larger than referral titles at
// the same Size setting (group `md` ≈ referral `lg`), so a page with both left at
// the default `md` shows a clear group > referral hierarchy. Base classes apply on
// the smallest containers (mobile); the @container variant bumps up on wide cards.
const REFERRAL_TITLE_SIZE_CLASSES: Record<Size, string> = {
  [Size.none]: 'text-base @xl:text-lg',
  [Size.xs]: 'text-base @xl:text-lg',
  [Size.sm]: 'text-lg @xl:text-xl',
  [Size.md]: 'text-xl @xl:text-2xl',
  [Size.lg]: 'text-2xl @xl:text-3xl',
  [Size.xl]: 'text-3xl @xl:text-4xl',
};

const GROUP_TITLE_SIZE_CLASSES: Record<Size, string> = {
  [Size.none]: 'text-lg @3xl:text-xl',
  [Size.xs]: 'text-lg @3xl:text-xl',
  [Size.sm]: 'text-xl @3xl:text-2xl',
  [Size.md]: 'text-2xl @3xl:text-3xl',
  [Size.lg]: 'text-3xl @3xl:text-4xl',
  [Size.xl]: 'text-4xl @3xl:text-5xl',
};

const TITLE_SIZE_ORDER = [Size.xs, Size.sm, Size.md, Size.lg, Size.xl];

// Compact layouts (cardless referrals, browser-bar group titles) render a step
// below the standard scale while still honoring the user's titleSize setting.
export function shiftSize(size: Size | undefined, shift: number): Size {
  const index = TITLE_SIZE_ORDER.indexOf(size && size !== Size.none ? size : Size.md);
  return TITLE_SIZE_ORDER[Math.min(Math.max(index + shift, 0), TITLE_SIZE_ORDER.length - 1)];
}

export function getTitleClasses(options?: { titleSize?: Size; titleWeight?: Weight; isGroupTitle?: boolean }) {
  const { titleSize, titleWeight, isGroupTitle } = options || {};
  const sizeClasses = (isGroupTitle ? GROUP_TITLE_SIZE_CLASSES : REFERRAL_TITLE_SIZE_CLASSES)[titleSize || Size.md];
  return `${getTitleWeightClass(titleWeight)} ${sizeClasses}`;
}

function getTitleWeightClass(weight?: Weight) {
  switch (weight) {
    case Weight.regular:
      return 'font-normal';
    case Weight.bold:
      return 'font-bold';
    case Weight.semibold:
    default:
      return 'font-semibold';
  }
}

export function getLineClampClass(clamp?: Size) {
  switch (clamp) {
    case Size.none:
      return 'line-clamp-none';
    case Size.xs:
      return 'line-clamp-1';
    case Size.sm:
      return 'line-clamp-2';
    case Size.lg:
      return 'line-clamp-5';
    case Size.xl:
      return 'line-clamp-[8]';
    case Size.md:
    default:
      return 'line-clamp-3';
  }
}

export type UpdateHandler<T, U = void> = (data: T, options?: UpdateOptions) => Promise<U>;

export type UpdateOptions = {
  updateId?: string;
  onBefore?: () => void | Promise<void>;
  onSuccess?: () => void | Promise<void>;
  onError?: (e: unknown) => void | Promise<void>;
};

export const REFERRAL_LIMIT = 5;

export function getNormalizedProperty<T>(valueInfo: {
  value?: T;
  defaultValue: T;
  options: Array<T>;
  enforceDefaults?: boolean;
}): T {
  const { value, defaultValue, options, enforceDefaults } = valueInfo;

  if (enforceDefaults) {
    return defaultValue;
  }

  return value !== undefined && options.includes(value) ? value : defaultValue;
}

export function getCleanedObject(obj: Record<string, unknown>): Record<string, unknown> {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  return Object.fromEntries(Object.entries(obj).filter(([_, value]) => value !== undefined));
}

export const copyUrl = (url?: string) => {
  const isUrl = url != null && /^https?:\/\//i.test(url);
  navigator.clipboard
    .writeText(url != null ? url : window.location.href)
    .then(() => toast.success(`${isUrl ? 'URL' : 'Value'} copied to clipboard!`, { position: 'bottom-left' }))
    .catch((err) => console.error('Failed to copy URL:', err));
};

export enum DiscountType {
  percent = 'percentage',
  fixed = 'fixed',
}
